import React, { useState } from 'react';
import SimpleEmailTemplateManager from '../components/settings/SimpleEmailTemplateManager';
import ApiKeyManager from '../components/settings/ApiKeyManager';
import SystemMaintenance from '../components/settings/SystemMaintenance';

// Simple SVG Icons
const EnvelopeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PaintBrushIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 5H3m0 6h4m-4 6h4m10-9l-4 4 2 2 4-4-2-2z" />
  </svg>
);

const Cog8ToothIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const KeyIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591L6.499 13.5c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  </svg>
);

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('email-templates');

  const tabs = [
    {
      id: 'email-templates',
      name: 'E-Mail Vorlagen',
      icon: EnvelopeIcon,
      description: 'Verwalten Sie alle E-Mail-Templates'
    },
    {
      id: 'api-keys',
      name: 'API-Keys',
      icon: KeyIcon,
      description: 'Verwalten Sie API-Keys für PIM-Integration'
    },
    {
      id: 'design',
      name: 'Design Einstellungen',
      icon: PaintBrushIcon,
      description: 'Anpassen des E-Mail-Designs'
    },
    {
      id: 'system',
      name: 'System Einstellungen',
      icon: Cog8ToothIcon,
      description: 'Allgemeine System-Konfiguration'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'email-templates':
        return <SimpleEmailTemplateManager />;
      case 'api-keys':
        return <ApiKeyManager />;
      case 'design':
        return (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Design Einstellungen</h3>
            <p className="text-gray-600">Design-Konfigurator wird hier implementiert...</p>
          </div>
        );
      case 'system':
        return <SystemMaintenance />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 w-full">

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${isActive
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <tab.icon
                  className={`
                    mr-2 h-5 w-5
                    ${isActive ? 'text-red-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                <div className="text-left">
                  <div>{tab.name}</div>
                  <div className="text-xs text-gray-400 font-normal">
                    {tab.description}
                  </div>
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Settings;